// Composable para formateo de fechas
// Extrae funcionalidad de formateo de useBaseModule

import { useI18n } from "vue-i18n";

/**
 * Composable para formatear fechas y horas en diferentes formatos
 */
export function useDateFormatter() {
  const { locale: i18nLocale } = useI18n();

  /**
   * Formatea una fecha como string localizado simple
   */
  function formatDate(value: Date | string | number): string {
    if (!value) return "";
    return new Date(value).toLocaleDateString();
  }

  /**
   * Formatea solo la fecha (sin hora) en formato dd/MM/yyyy
   */
  function formatOnlyDate(value: Date | string | number): string {
    if (!value) return "";
    return new Date(value).toLocaleString([i18nLocale.value, "en"], {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  }

  /**
   * Formatea fecha completa con nombre del día y mes
   * Ejemplo: "Lunes, 22 de octubre de 2025"
   */
  function formatFullDate(value: Date | string | number): string {
    if (!value) return "";
    const result = new Date(value).toLocaleString([i18nLocale.value, "en"], {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    return result.charAt(0).toUpperCase() + result.slice(1);
  }

  /**
   * Formatea fecha larga en formato dd/MM/yyyy
   */
  function formatLongDate(value: Date | string | number): string {
    if (!value) return "";
    return new Date(value).toLocaleString([i18nLocale.value, "en"], {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  }

  /**
   * Formatea fecha corta en formato dd/MM/yy
   */
  function formatShortDate(value: Date | string | number): string {
    if (!value) return "";
    return new Date(value).toLocaleString([i18nLocale.value, "en"], {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
    });
  }

  /**
   * Formatea fecha y hora larga en formato dd/MM/yyyy HH:mm
   */
  function formatLongDateTime(
    value: Date | string | number,
    is24hours?: boolean
  ): string {
    if (!value) return "";
    return new Date(value).toLocaleString([i18nLocale.value, "en"], {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: is24hours ? false : undefined,
    });
  }

  /**
   * Formatea fecha y hora corta en formato dd/MM/yy HH:mm
   */
  function formatShortDateTime(
    value: Date | string | number,
    is24hours?: boolean
  ): string {
    if (!value) return "";
    return new Date(value).toLocaleString([i18nLocale.value, "en"], {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: is24hours ? false : undefined,
    });
  }

  /**
   * Formatea solo la hora en formato HH:mm
   */
  function formatTime(
    value: Date | string | number,
    is24hours?: boolean
  ): string {
    if (!value) return "";
    return new Date(value).toLocaleString([i18nLocale.value, "en"], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: is24hours ? false : undefined,
    });
  }

  return {
    formatDate,
    formatOnlyDate,
    formatFullDate,
    formatLongDate,
    formatShortDate,
    formatLongDateTime,
    formatShortDateTime,
    formatTime,
  };
}
